const DEBUGGING: boolean = process.env.NODE_ENV === 'development'

import * as utils from './utils'
import * as utils_topshot from './utils_topshot'
import { BaseObject, MongoObject } from './_baseClasses'
import { User as TopShotUser } from './topshot.d'
import { CollectorScore as CollectorScoreTS } from './topshot.d'
import { Showcase, UserChallenge } from './topshot.d'

export interface SetCS  {
  setID: number,
  score: number,
  moments: number
}

export interface PlayerCS {
  playerID: number,
  score: number,
  moments: number
}

export interface TeamCS {
  teamID: number,
  score: number,
  moments: number
}

export interface TeamBonus {
  teamName: string,
  bonus: number
}

interface SetBonus {
  setID: number,
  setName: string,
  bonus: number
}

interface CollectorScore {
  numMoments: number,
  total: number,
  common: number,
  fandom: number,
  rare: number,
  legendary: number,
  challengeCompletionBonus: number,
  setBonuses: SetBonus[],
  teamBonuses: TeamBonus[],
  teamCSs: TeamCS[],                // Dec 5, 2021
  playerCSs: PlayerCS[],            // Dec 11, 2021
  setCSs: SetCS[],                  // Dec 11, 2021
  dropBonus: number,
  dropBonusWNBA: number,
  allStarNFTBonus: number,
  rank: number,                     // May 19, 2022
  needsRecalculating: boolean
}

interface CollectorScore2 {
  numMoments: number,
  total: number,
  common: number,
  fandom: number,
  rare: number,
  legendary: number,
  challengeCompletionBonus: number,
  setBonuses: SetBonus[],
  teamBonuses: TeamBonus[],
  teamCSs: TeamCS[],
  playerCSs: PlayerCS[],
  setCSs: SetCS[],
  dropBonus: number,
  badgeBonuses: number,
  marketplaceSpendBonuses: number,
  allStarNFTBonus: number,
  needsRecalculating: boolean,
  version: number,
  whenCalculated: Date
}

interface CollectorScore2b {
  numMoments: number,
  total: number,
  setBonuses: SetBonus[],
  teamBonuses: TeamBonus[],
  teamCSs: TeamCS[],
  playerCSs: PlayerCS[],
  setCSs: SetCS[],
  dropBonus: number,
  allStarNFTBonus: number,
  needsRecalculating: boolean,
  version: number,
  whenCalculated: Date,                         // last time full calculation was done
  rank: number,                                 // May 19, 2022

  runningTotal: number,                         // for keeping track of total CS in an incremental way
  numberOfChangesSinceFullCalculation: number   // for fun, keep track of how many changes have been processed in runningTotal since last full calculation
}

// Locked TSS - like CollectorScore2b but only locked moments
// Not sure yet if I'm going to calculate leaderboards in here inside AccountExtra and AccountExtraSnapshot
//  It would be parallel I guess, but I need to remembrer how I update leaderboards when folks buy stuff (or in this case lock stuff)
//  I think on the 23:10 daily cron.
//  I would need to add teamCSs to AccountExtra
//    * might stop doing AccountExtraSnapshot -- really only useful for tracking deltas which I did but never even showed for favorite team snapshots
interface TopshotScoreLocked {
  numMoments: number,
  total: number,
  setBonuses: SetBonus[],
  teamBonuses: TeamBonus[],
  teamCSs: TeamCS[],
  playerCSs: PlayerCS[],
  setCSs: SetCS[],
  needsRecalculating: boolean,
  version: number,
  whenCalculated: Date,                         // last time full calculation was done
  rank: number
}

export interface MarketplaceSpend {
  spend: number,
  revenue: number,
  net: number,
  netMay312021: number,
  netJan31ToJune1021: number,
  giftBackDec16: {                        // Dec 16, 2021 -- for Top Shot Gift Back (5% MP fee not subtracted out of revenue)
    spend: number,
    revenue: number,
    revenueMinusFees: number,
    net: number
  },
  netJan012022: {                         // March 21, 2022 - Bryan Thill asked me to do this for TS
    spend: number,
    revenue: number,
    revenueMinusFees: number,
    net: number
  },
  netJan012023: {                        // Jan 23, 2023
    spend: number,
    revenue: number,
    revenueMinusFees: number,
    net: number
  },
  spendOnStillOwnedMoments: number,       // introduced July 9, 2021
  needsRecalculating: boolean
}

interface AuctionScore {
  successfulSold: number,
  successfulBought: number,
  total: number
}

// peer to peer feedback rating
interface FeedbackRating {
  num: number,
  sum: number,
  average: number,                  // defined to be sum/num
  needsRecalculating: boolean       // never goes to true in practice
}

export interface FavoriteTeamChange {
  oldTeamID: string,
  newTeamID: string,
  when: Date
}
export class Account extends MongoObject {
  protected static collectionName: string = 'accounts'           // IMPORTANT

  public flowAddress: string = null
  public username: string = null               // can be null if we don't know it or someone else took it
  public usernameLowercase: string = null      // can be null if we don't know it or someone else took it
  public dapperID: string = null
  public profileImageUrl: string = null
  public twitterHandle: string = null
  public createdAt: Date = null
  public favoriteTeamID: string = null         // June 23, 2021
  public usernameHistory: string[] = []
  public whenHarvestedMomentGUIDs: Date = null

  public collectorScore: CollectorScore = null
  public collectorScore2: CollectorScore2 = null
  public collectorScore2b: CollectorScore2b = null    // Apr 26, 2022
  public topShotScoreLocked: TopshotScoreLocked = null    // Aug 23, 2022
  public marketplaceSpend: MarketplaceSpend = null

  public feedbackRating: FeedbackRating = null

  public lastActiveDate: Date = null          // for stats purposes (figuring out active users and predicting queue sizes based on that)
  public dateOfFirstPurchase: Date = null     // good for gifting privileges

  // topShotUser is not that important, now that I have favoriteTeamID which is actually more accurate than topShotUser.favorite.
  //  topShotUser isn't updated very often -- maybe once they fix BallerStatus it will be?
  public topShotUser: TopShotUser = null
  public topShotUserLastFetched: Date = null

  public collectorScoreTS: CollectorScoreTS = null
  public collectorScoreTSLastFetched: Date = null

  public showcases: Showcase[] = null
  public showcasesLastFetched: Date = null

  // showcases are defunct so moving to these
  public userChallenges: UserChallenge[] = null
  public userChallengesLastFetched: Date = null

  public auctionScore: AuctionScore = null

  public eab: number = null                            // one-time bonus May 17, 2022 introduced with TSS

  // Jan 14, 2022
  public favoriteTeamLastChecked: Date = null

  // Feb 13, 2024
  public favoriteTeamChanges: FavoriteTeamChange[] = null
  

  // complex, class-based fields (require to be hydrated after loading from external sources like mongo or elastic)

  constructor(po?: object) {
    super()
    if (po) this.injectPlainObject(po)
    this.hydrate()
  }

  protected hydrate(): void {
    if (typeof this.createdAt === 'string') this.createdAt = new Date(this.createdAt)
    if (typeof this.whenHarvestedMomentGUIDs === 'string') this.whenHarvestedMomentGUIDs = new Date(this.whenHarvestedMomentGUIDs)
    if (typeof this.topShotUserLastFetched === 'string') this.topShotUserLastFetched = new Date(this.topShotUserLastFetched)
    if (typeof this.lastActiveDate === 'string') this.lastActiveDate = new Date(this.lastActiveDate)
    if (typeof this.dateOfFirstPurchase === 'string') this.dateOfFirstPurchase = new Date(this.dateOfFirstPurchase)
    if (typeof this.collectorScoreTSLastFetched === 'string') this.collectorScoreTSLastFetched = new Date(this.collectorScoreTSLastFetched)
    if (typeof this.showcasesLastFetched === 'string') this.showcasesLastFetched = new Date(this.showcasesLastFetched)
    if (typeof this.userChallengesLastFetched === 'string') this.userChallengesLastFetched = new Date(this.userChallengesLastFetched)
    if (this.collectorScore2 && this.collectorScore2.whenCalculated && typeof this.collectorScore2.whenCalculated === 'string') this.collectorScore2.whenCalculated = new Date(this.collectorScore2.whenCalculated)
    if (this.collectorScore2b && this.collectorScore2b.whenCalculated && typeof this.collectorScore2b.whenCalculated === 'string') this.collectorScore2b.whenCalculated = new Date(this.collectorScore2b.whenCalculated)
    if (this.topShotScoreLocked && this.topShotScoreLocked.whenCalculated && typeof this.topShotScoreLocked.whenCalculated === 'string') this.topShotScoreLocked.whenCalculated = new Date(this.topShotScoreLocked.whenCalculated)
    super.hydrate()
  }

  public isSafe(): boolean {
    if (this.dapperID) return false
    if (this.usernameLowercase) return false
    if (this.usernameHistory) return false
    if (this.whenHarvestedMomentGUIDs) return false
    if (this.topShotUserLastFetched) return false
    if (this.topShotUser) return false
    if (this.collectorScoreTS) return false
    if (this.showcases) return false
    if (this.userChallenges) return false
    if (this.favoriteTeamChanges) return false
    return true
  }

  public getNiceCollectorScore(): string {
    if (!this.collectorScore) return null
    return utils_topshot.getNiceCollectorScore(this.collectorScore.total)
  }

  public getNiceDropBonusScore(): string {
    if (!this.collectorScore) return null
    if (this.collectorScore.dropBonus < 10000) return this.collectorScore.dropBonus.toString()
    if (this.collectorScore.dropBonus < 100000) return (this.collectorScore.dropBonus / 1000).toFixed(1) + 'K'
    if (this.collectorScore.dropBonus < 1000000) return (this.collectorScore.dropBonus / 1000).toFixed(0) + 'K'
    return (this.collectorScore.dropBonus / 1000000).toFixed(1) + 'M'
  }  

  public getTotalEffectiveScore(): string {
    if (!this.collectorScore) return null
    const total: number = this.collectorScore.total + this.collectorScore.dropBonus
    if (total < 10000) return total.toString()
    if (total < 100000) return (total / 1000).toFixed(1) + 'K'
    if (total < 1000000) return (total / 1000).toFixed(0) + 'K'
    return (total / 1000000).toFixed(1) + 'M'
  } 

  public getClassNameBasedOnScore() {
    return utils_topshot.getClassNameBasedOnScore(this.collectorScore.total)
  }

  public getNiceNetMarketPlaceSpend(): string {
    if (!this.marketplaceSpend) return null
    const negative: boolean = this.marketplaceSpend.net < 0
    const value: number = Math.abs(this.marketplaceSpend.net)
    if (value < 10000) return (negative ? '-' : '') + '$' + Math.round(value)
    if (value < 100000) return (negative ? '-' : '') + '$' + (value / 1000).toFixed(1) + 'K'
    if (value < 1000000) return (negative ? '-' : '') + '$' + (value / 1000).toFixed(0) + 'K'
    return (negative ? '-' : '') + '$' + (value / 1000000).toFixed(1) + 'M'
  }

  public getNiceNetMarketPlaceSpendTimeCutoff(): string {
    if (!this.marketplaceSpend) return null
    const negative: boolean = this.marketplaceSpend.netJan31ToJune1021 < 0
    const value: number = Math.abs(this.marketplaceSpend.netJan31ToJune1021)
    if (value < 10000) return (negative ? '-' : '') + '$' + Math.round(value)
    if (value < 100000) return (negative ? '-' : '') + '$' + (value / 1000).toFixed(1) + 'K'
    if (value < 1000000) return (negative ? '-' : '') + '$' + (value / 1000).toFixed(0) + 'K'
    return (negative ? '-' : '') + '$' + (value / 1000000).toFixed(1) + 'M'
  }

  public getNiceMarketPlaceSpend(): string {
    if (!this.marketplaceSpend) return null
    const value: number = this.marketplaceSpend.spendOnStillOwnedMoments
    if (value < 10000) return '$' + Math.round(value)
    if (value < 100000) return '$' + (value / 1000).toFixed(1) + 'K'
    if (value < 1000000) return '$' + (value / 1000).toFixed(0) + 'K'
    return '$' + (value / 1000000).toFixed(1) + 'M'
  }  

  // fix for following
  public getFavoriteTeam(): string {
    const mascot: string = Account.getMascotFromTeamID(Number(this.favoriteTeamID))
    return mascot ? mascot : ''
    // if (!this.topShotUser) return ''
    // let favoriteToUse: string = this.topShotUser.favorite
    // if (!favoriteToUse && this.topShotUser.following && this.topShotUser.following.length > 0) favoriteToUse = this.topShotUser.following[0]
    // return utils.capitalizeFirstLetter(favoriteToUse)
  }

  public static getMascotFromTeamID(teamID: number): string {
    switch (teamID) {
      case 1610612737: return 'Hawks'
      case 1610612738: return 'Celtics'
      case 1610612751: return 'Nets'
      case 1610612766: return 'Hornets'
      case 1610612741: return 'Bulls'
      case 1610612739: return 'Cavaliers'
      case 1610612742: return 'Mavericks'
      case 1610612743: return 'Nuggets'
      case 1610612765: return 'Pistons'
      case 1610612744: return 'Warriors'
      case 1610612745: return 'Rockets'
      case 1610612754: return 'Pacers'
      case 1610612746: return 'Clippers'
      case 1610612747: return 'Lakers'
      case 1610612763: return 'Grizzlies'
      case 1610612748: return 'Heat'
      case 1610612749: return 'Bucks'
      case 1610612750: return 'Timberwolves'
      case 1610612740: return 'Pelicans'
      case 1610612752: return 'Knicks'
      case 1610612760: return 'Thunder'
      case 1610612753: return 'Magic'
      case 1610612755: return '76ers'
      case 1610612756: return 'Suns'
      case 1610612757: return 'Blazers'
      case 1610612758: return 'Kings'
      case 1610612759: return 'Spurs'
      case 1610612761: return 'Raptors'
      case 1610612762: return 'Jazz'
      case 1610612764: return 'Wizards'
    }
    return null
  }

  public daysSinceFirstPurchase(): number {
    if (!this.dateOfFirstPurchase) return null
    const msSinceFirstPurchase: number = new Date().getTime() - this.dateOfFirstPurchase.getTime()
    return msSinceFirstPurchase / (86400 * 1000)
  }

  public static getPortfolioLink(flowAddress: string): string {
    return `/community-tools/account?address=${utils_topshot.sansPrefix(flowAddress)}&mode=portfolio`
  }
  public static getAuctionsLink(flowAddress: string): string {
    return `/community-tools/account?address=${utils_topshot.sansPrefix(flowAddress)}&mode=myauctions`
  }  
  public static getFeedbackLink(flowAddress: string): string {
    return `/community-tools/account?address=${utils_topshot.sansPrefix(flowAddress)}&mode=feedback`
  }

}

(async () => {
})();
